import React, { useCallback, useContext, useRef, useState } from "react";
import primaryComponents from "../../primaryComponents";
import Icon from "../../../assets/icons";
import Images from "../../../assets/images";
import styles from "./ProductImageUploader.module.scss";
import { ToastContext } from "../../../hooks/context";

const ProductImageUploader = ({
  images = [],
  onImagesChange,
  onUpload,
  onDeleteSaved,
  onReorder,
  itemId,
  itemType,
  isUploading = false,
  buttonLabel = "Add Product Images",
  accept = "image/*",
  multiple = true,
  uploadIcon,
  deleteIcon,
  buttonClassNames,
  buttonStyle,
}) => {
  const inputRef = useRef(null);
  const triggerToast = useContext(ToastContext);
  const dragIndex = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleFileChange = useCallback(
    (e) => {
      if (onUpload) onUpload(e);
      e.target.value = "";
    },
    [onUpload],
  );

  const handleDelete = useCallback(
    (image) => {
      if (image.url || image.id) {
        onImagesChange((prev) => prev.filter((item) => item.id !== image.id));
        if (onDeleteSaved) onDeleteSaved(image.id);
      } else {
        onImagesChange((prev) =>
          prev.filter((item) => item.preview !== image.preview),
        );
      }
    },
    [onImagesChange, onDeleteSaved],
  );

  const handleSaveOrder = useCallback(
    (reorderedImages) => {
      const orderedMediaIds = reorderedImages
        .filter((img) => img.id)
        .map((img) => Number(img.id));

      if (orderedMediaIds.length === 0) return;

      if (!itemId || itemId === "undefined") return;

      onReorder(
        {
          item_id: itemId,
          item_type: itemType,
          ordered_media_ids: orderedMediaIds,
        },
        {
          onSuccess: () => {
            triggerToast("Image order saved successfully", "success");
          },
        },
      );
    },
    [itemId, itemType, onReorder, triggerToast],
  );

  const handleDragStart = useCallback((e, index) => {
    dragIndex.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index);
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e, index) => {
      e.preventDefault();

      if (dragIndex.current === null || dragIndex.current === index) {
        setDragOverIndex(null);
        dragIndex.current = null;
        return;
      }

      const reordered = [...images];
      const [moved] = reordered.splice(dragIndex.current, 1);
      reordered.splice(index, 0, moved);

      onImagesChange(reordered);
      setDragOverIndex(null);
      dragIndex.current = null;

      handleSaveOrder(reordered);
    },
    [images, onImagesChange, handleSaveOrder],
  );

  const handleDragEnd = useCallback(() => {
    dragIndex.current = null;
    setDragOverIndex(null);
  }, []);

  const handleButtonClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  return (
    <div className={styles.topFormSection__right}>
      <input
        style={{ display: "none" }}
        ref={inputRef}
        type="file"
        name="product-img"
        id="product-img"
        accept={accept}
        multiple={multiple}
        onChange={handleFileChange}
      />

      <primaryComponents.Button
        classNames={buttonClassNames ?? `btn btn--outline ${styles.uploadBtn}`}
        style={buttonStyle}
        onClick={handleButtonClick}
        isLoading={isUploading}
        loadingText="uploading..."
        primarySpinner={false}
        type="button"
      >
        {uploadIcon ?? <img className="mr-1" src={Icon.addCriclePlus} alt="" />}
        {buttonLabel}
      </primaryComponents.Button>

      <div className={styles.imagesContainer}>
        {images.length > 0 ? (
          images.map((image, index) => (
            <div
              key={image.id ?? image.preview ?? index}
              className={`${styles.imageDeleteContainer} ${
                dragOverIndex === index ? styles.dragOver : ""
              }`}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              style={{
                cursor: "grab",
                opacity: dragOverIndex === index ? 0.5 : 1,
              }}
            >
              <div
                className={styles.imageContainer}
                style={{ pointerEvents: "none" }}
              >
                <img
                  src={image.url ?? image.preview ?? Images.product_avatar}
                  alt=""
                />
                {!image.id && !image.url && (
                  <div className={styles.uploadingOverlay}>
                    <span>uploading...</span>
                  </div>
                )}
              </div>
              <div
                className={styles.deleteContainer}
                style={{ pointerEvents: "auto" }}
                onClick={() => handleDelete(image)}
              >
                {deleteIcon ?? <img src={Icon.deleteIcon} alt="" />}
              </div>
            </div>
          ))
        ) : (
          <div className={styles.imageDeleteContainer}>
            <div className={styles.imageContainer}>
              <img src={Images.product_avatar} alt="" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ProductImageUploader);
